<template>
  <v-container fluid>
    <!-- Title -->
    <v-row no-gutters>
      <v-col cols="12">
        <v-card color="transparent" flat dark>
          <div class="text-h4 font-weight-bold text-center" style="padding: 5vh">SMS Logs</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Body -->
    <v-row no-gutters>
      <v-col cols="12">
        <v-row align="center" justify="center">
          <v-card rounded="lg" width="90%" class="pa-2">
            <v-card-subtitle style="display: inline-block">SMS Log List (Total #{{ total_record }} Records)</v-card-subtitle>

            <v-card-text>
              <!-- Filters -->
              <v-row align="center">
                <!-- Date Range -->
                <v-col cols="12" sm="6" md="3" class="py-1">
                  <v-menu ref="menu" v-model="date_picker.menu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field color="grey" hide-details dense outlined v-model="date_range" label="Date Range" prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on"></v-text-field>
                    </template>
                    <v-date-picker color="#2fcfa6" landscape ref="picker" v-model="date_picker.date" range></v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Status -->
                <v-col cols="12" sm="6" md="2" class="py-1">
                  <v-select outlined dense hide-details label="Send Status" v-model="filters.status" :items="smsLogs.status_list" item-text="title" item-value="value"></v-select>
                </v-col>

                <!-- Delivery Status -->
                <v-col cols="12" sm="6" md="2" class="py-1">
                  <v-select outlined dense hide-details label="Delivery Status" v-model="filters.delivery_status" :items="smsLogs.delivery_status_list" item-text="title" item-value="value"></v-select>
                </v-col>

                <!-- Service Type -->
                <v-col cols="12" sm="6" md="3" class="py-1">
                  <v-select
                    outlined
                    dense
                    hide-details
                    clearable
                    label="Service Type"
                    v-model="filters.service_type_id"
                    :items="general.queues"
                    item-text="service_type"
                    item-value="service_type_id"
                  ></v-select>
                </v-col>

                <!-- Tel -->
                <v-col cols="12" sm="6" md="2" class="py-1">
                  <v-text-field outlined dense hide-details label="Tel" v-model="filters.tel"></v-text-field>
                </v-col>

                <!-- Code -->
                <v-col cols="12" sm="6" md="2" class="py-1">
                  <v-text-field outlined dense hide-details label="Ticket Code" v-model="filters.code"></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" class="py-2">
                  <v-btn block depressed @click="search">
                    <v-icon left small>mdi-magnify</v-icon>
                    Search
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Table -->
              <v-data-table class="mt-2" :headers="smsLogs.headers" :items="smsLogs.items" :items-per-page="25">
                <!-- Index -->
                <template v-slot:[`item.index`]="{ item }">
                  {{ smsLogs.items.indexOf(item) + 1 }}
                </template>

                <!-- Send Time -->
                <template v-slot:[`item.create_datetime`]="{ item }">
                  {{ item.create_datetime ? $moment(item.create_datetime).format('YYYY-MM-DD HH:mm:ss') : '-' }}
                </template>

                <!-- Status -->
                <template v-slot:[`item.status`]="{ item }">
                  <v-chip x-small label :color="sendStatusColor(item.status)" dark>{{ item.status }}</v-chip>
                </template>

                <!-- Delivery Status -->
                <template v-slot:[`item.delivery_status`]="{ item }">
                  <v-chip x-small label :color="deliveryStatusColor(item.delivery_status)" dark>{{ item.delivery_status || 'unknown' }}</v-chip>
                </template>

                <!-- Delivery Updated At -->
                <template v-slot:[`item.delivery_updated_at`]="{ item }">
                  {{ item.delivery_updated_at ? $moment(item.delivery_updated_at).format('YYYY-MM-DD HH:mm:ss') : '-' }}
                </template>

                <!-- Ticket Code -->
                <template v-slot:[`item.code`]="{ item }">
                  {{ item.code || '-' }}
                </template>

                <!-- Service Type / Prefix -->
                <template v-slot:[`item.service_type`]="{ item }">
                  {{ serviceTypeDisplay(item.service_type_id) }}
                </template>

                <!-- Tel (masked server-side; rendered as received) -->
                <template v-slot:[`item.tel`]="{ item }">
                  {{ item.tel || '-' }}
                </template>

                <!-- Message Preview -->
                <template v-slot:[`item.message`]="{ item }">
                  <span :title="item.message" class="text-truncate d-inline-block" style="max-width: 260px">{{ item.message || '-' }}</span>
                </template>

                <!-- Provider / Error -->
                <template v-slot:[`item.provider_error`]="{ item }">
                  <span :title="providerError(item)" class="text-truncate d-inline-block" style="max-width: 260px">{{ providerError(item) }}</span>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import bookingDisplay from '@/mixins/bookingDisplay'

export default {
  name: 'SmsLogs',
  mixins: [bookingDisplay],
  data() {
    return {
      date_picker: {
        menu: false,
        date: [this.$moment().format('YYYY-MM-DD'), this.$moment().format('YYYY-MM-DD')]
      },
      filters: {
        status: null,
        delivery_status: null,
        service_type_id: null,
        tel: null,
        code: null
      }
    }
  },
  computed: {
    ...mapState(['smsLogs', 'general']),
    date_range() {
      if (this.date_picker.date) return this.date_picker.date.join(' ~ ')
      return null
    },
    total_record() {
      return this.smsLogs.items.length || ''
    }
  },
  methods: {
    ...mapActions(['fetchSmsLogs']),
    ...mapMutations(['SET_SNACKBAR']),
    providerError(item) {
      return item.error_message || item.provider_message || '-'
    },
    sendStatusColor(status) {
      const map = { success: 'success', pending: 'grey', failed: 'error' }
      return map[status] || 'grey'
    },
    // 'unknown' is what the backend records when the gateway returns no message id,
    // i.e. delivery genuinely cannot be confirmed - it is not an error.
    deliveryStatusColor(status) {
      const map = { delivered: 'success', pending: 'blue-grey', failed: 'error', undelivered: 'orange', unknown: 'grey' }
      return map[status] || 'grey'
    },
    search() {
      const params = {}
      if (this.date_picker.date && this.date_picker.date.length === 2) {
        params.from_date = this.date_picker.date[0]
        params.to_date = this.date_picker.date[1]
      }
      if (this.filters.status) params.status = this.filters.status
      if (this.filters.delivery_status) params.delivery_status = this.filters.delivery_status
      if (this.filters.service_type_id) params.service_type_id = this.filters.service_type_id
      if (this.filters.tel) params.tel = this.filters.tel
      if (this.filters.code) params.code = this.filters.code
      this.fetchSmsLogs(params)
    }
  },
  watch: {
    'date_picker.date': {
      handler() {
        if (this.date_picker.date && this.date_picker.date.length === 2) {
          this.date_picker.menu = false
        }
      }
    }
  },
  mounted() {
    this.search()
  }
}
</script>
